<template>
  <div v-if="show" class="app-modal send-email-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog send-email-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-email-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="send-email-modal-title">{{ resolvedText.title }}</h3>
          <p>{{ resolvedText.subtitle }}</p>
        </div>

        <button
          class="app-modal__close"
          type="button"
          :aria-label="resolvedText.closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content send-email-modal__content">
        <form class="app-modal__form" @submit.prevent="emitSubmit">
          <div class="send-email-modal__layout">
            <aside class="send-email-modal__summary-pane">
              <div class="send-email-modal__summary-card">
                <h4>{{ resolvedText.summaryPanelTitle }}</h4>

                <dl class="send-email-modal__summary-grid">
                  <dt>{{ summaryData.sendTypeLabel }}</dt>
                  <dd>{{ summaryData.sendTypeValue }}</dd>

                  <dt>{{ summaryData.counterpartLabel }}</dt>
                  <dd>{{ summaryData.counterpartValue }}</dd>

                  <dt>{{ summaryData.totalLabel }}</dt>
                  <dd>{{ summaryData.totalValue }}</dd>

                  <dt>{{ resolvedText.summaryToLabel }}</dt>
                  <dd>{{ summaryToValue }}</dd>

                  <dt>{{ resolvedText.summaryCopyLabel }}</dt>
                  <dd>{{ summaryCopyValue }}</dd>
                </dl>

                <div class="send-email-modal__summary-attachments">
                  <strong>{{ resolvedText.summaryAttachmentsLabel }}</strong>

                  <div v-if="summaryAttachmentItems.length" class="send-email-modal__attachment-list">
                    <span
                      v-for="attachment in summaryAttachmentItems"
                      :key="`${attachment.group}-${attachment.id}`"
                      class="send-email-modal__attachment-chip"
                    >
                      <i class="fa fa-paperclip" aria-hidden="true"></i>
                      <span>{{ attachment.label }}</span>
                      <button
                        v-if="attachment.group !== 'invoice-default'"
                        type="button"
                        class="send-email-modal__attachment-remove"
                        :title="resolvedText.removeAttachmentLabel"
                        @click="removeAttachment(attachment)"
                      >
                        x
                      </button>
                    </span>
                  </div>

                  <p v-else class="send-email-modal__summary-empty">{{ resolvedText.noAttachmentsSelected }}</p>
                </div>
              </div>
            </aside>

            <section class="send-email-modal__send-pane">
              <h4>{{ resolvedText.sendPanelTitle }}</h4>

              <div class="app-modal-field full-width">
                <span>{{ resolvedText.toLabel }}</span>

                <div class="send-email-modal__recipient-input" @click="focusRecipientField('to')">
                  <span
                    v-for="recipient in toRecipients"
                    :key="`to-recipient-${recipient.id}`"
                    class="send-email-modal__recipient-chip"
                  >
                    <span>{{ recipient.label }}</span>
                    <button
                      type="button"
                      class="send-email-modal__recipient-remove"
                      :title="resolvedText.removeRecipientLabel"
                      @click.stop="removeRecipient('to', recipient.id)"
                    >
                      x
                    </button>
                  </span>

                  <input
                    ref="toInput"
                    v-model.trim="toQuery"
                    type="text"
                    class="send-email-modal__recipient-text-input"
                    :placeholder="toRecipients.length === 0 ? resolvedText.toPlaceholder : ''"
                    @focus="openRecipientMenu('to')"
                    @input="openRecipientMenu('to')"
                    @blur="scheduleRecipientMenuClose"
                    @keydown.enter.prevent="confirmRecipientInput('to')"
                    @keydown.tab.prevent="confirmRecipientInput('to')"
                    @keydown.backspace="handleRecipientBackspace('to', $event)"
                  />
                </div>

                <small v-if="recipientsLoading && activeRecipientField === 'to'" class="send-email-modal__recipient-loading">
                  {{ resolvedText.recipientsLoadingLabel }}
                </small>

                <div v-if="showRecipientMenu('to')" class="send-email-modal__recipient-menu" @mousedown.prevent>
                  <button
                    v-for="option in filteredToOptions"
                    :key="`to-option-${option.id}`"
                    type="button"
                    class="send-email-modal__recipient-option"
                    @click="toggleRecipientFromMenu('to', option)"
                  >
                    <div>
                      <strong>{{ option.name }}</strong>
                      <small>{{ option.email }}</small>
                    </div>
                    <i
                      class="fa"
                      :class="isRecipientSelected('to', option.id) ? 'fa-check-square' : 'fa-square-o'"
                      aria-hidden="true"
                    ></i>
                  </button>

                  <button
                    v-if="canCreateExternalTo"
                    type="button"
                    class="send-email-modal__recipient-option send-email-modal__recipient-option--external"
                    @click="addExternalRecipient('to')"
                  >
                    <div>
                      <strong>{{ resolvedText.addExternalRecipientLabel }}</strong>
                      <small>{{ normalizedToQueryEmail }}</small>
                    </div>
                    <i class="fa fa-plus" aria-hidden="true"></i>
                  </button>

                  <p v-if="!filteredToOptions.length && !canCreateExternalTo" class="send-email-modal__summary-empty">
                    {{ resolvedText.noRecipientMatches }}
                  </p>
                </div>
              </div>

              <div class="app-modal-field full-width">
                <span>{{ resolvedText.copyLabel }}</span>

                <div class="send-email-modal__recipient-input" @click="focusRecipientField('cc')">
                  <span
                    v-for="recipient in ccRecipients"
                    :key="`cc-recipient-${recipient.id}`"
                    class="send-email-modal__recipient-chip"
                  >
                    <span>{{ recipient.label }}</span>
                    <button
                      type="button"
                      class="send-email-modal__recipient-remove"
                      :title="resolvedText.removeRecipientLabel"
                      @click.stop="removeRecipient('cc', recipient.id)"
                    >
                      x
                    </button>
                  </span>

                  <input
                    ref="ccInput"
                    v-model.trim="ccQuery"
                    type="text"
                    class="send-email-modal__recipient-text-input"
                    :placeholder="ccRecipients.length === 0 ? resolvedText.copyPlaceholder : ''"
                    @focus="openRecipientMenu('cc')"
                    @input="openRecipientMenu('cc')"
                    @blur="scheduleRecipientMenuClose"
                    @keydown.enter.prevent="confirmRecipientInput('cc')"
                    @keydown.tab.prevent="confirmRecipientInput('cc')"
                    @keydown.backspace="handleRecipientBackspace('cc', $event)"
                  />
                </div>

                <div v-if="showRecipientMenu('cc')" class="send-email-modal__recipient-menu" @mousedown.prevent>
                  <button
                    v-for="option in filteredCcOptions"
                    :key="`cc-option-${option.id}`"
                    type="button"
                    class="send-email-modal__recipient-option"
                    @click="toggleRecipientFromMenu('cc', option)"
                  >
                    <div>
                      <strong>{{ option.name }}</strong>
                      <small>{{ option.email }}</small>
                    </div>
                    <i
                      class="fa"
                      :class="isRecipientSelected('cc', option.id) ? 'fa-check-square' : 'fa-square-o'"
                      aria-hidden="true"
                    ></i>
                  </button>

                  <button
                    v-if="canCreateExternalCc"
                    type="button"
                    class="send-email-modal__recipient-option send-email-modal__recipient-option--external"
                    @click="addExternalRecipient('cc')"
                  >
                    <div>
                      <strong>{{ resolvedText.addExternalRecipientLabel }}</strong>
                      <small>{{ normalizedCcQueryEmail }}</small>
                    </div>
                    <i class="fa fa-plus" aria-hidden="true"></i>
                  </button>

                  <p v-if="!filteredCcOptions.length && !canCreateExternalCc" class="send-email-modal__summary-empty">
                    {{ resolvedText.noRecipientMatches }}
                  </p>
                </div>
              </div>

              <div class="app-modal-field full-width">
                <AppRichTextEditorField
                  v-model="messageHtml"
                  :text="resolvedText"
                  label-class="send-email-modal__message-label"
                  :disabled="submitting"
                  @update:textValue="handleMessageTextUpdate"
                />
              </div>

              <component
                :is="activeContextSectionComponent"
                v-if="activeContextSectionComponent"
                :text="resolvedText"
                :show-invoice-attachments-action="canSelectInvoiceAttachments"
                :show-invoice-attachments="showInvoiceAttachments"
                :show-document-attachments="showDocumentAttachments"
                :is-existing-document-mode="isExistingDocumentMode"
                :selected-invoice-attachments="selectedInvoiceAttachments"
                :normalized-invoice-attachment-options="normalizedInvoiceAttachmentOptions"
                :selected-document-attachments="selectedDocumentAttachments"
                :normalized-document-attachment-options="normalizedDocumentAttachmentOptions"
                :new-document-uploads="newDocumentUploads"
                @toggle-invoice-attachments="toggleInvoiceAttachmentsSection"
                @toggle-document-attachments="toggleDocumentAttachmentsSection"
                @close-invoice-attachments="closeInvoiceAttachmentsSection"
                @close-document-attachments="closeDocumentAttachmentsSection"
                @set-document-attachment-mode="setDocumentAttachmentMode"
                @update:selected-invoice-attachments="selectedInvoiceAttachments = $event"
                @update:selected-document-attachments="selectedDocumentAttachments = $event"
                @new-document-change="handleNewDocumentChange"
                @add-new-document-upload-row="addNewDocumentUploadRow"
                @remove-new-document-upload-row="removeNewDocumentUploadRow"
              />
            </section>
          </div>

          <footer class="app-modal__footer send-email-modal__footer">
            <div class="app-modal__footer-right">
              <button type="button" class="btn btn-light" :disabled="submitting" @click="emitClose">
                {{ resolvedText.cancelLabel }}
              </button>

              <button type="submit" class="btn btn-primary" :disabled="submitting || !canSubmit">
                {{ submitting ? resolvedText.submittingLabel : resolvedText.submitLabel }}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
import AppRichTextEditorField from '@/components/common/AppRichTextEditorField.vue';
import SendEmailInvoiceAttachmentsSection from '@/components/sendEmail/contexts/SendEmailInvoiceAttachmentsSection.vue';
import {
  createSendEmailModalState,
  sendEmailModalComputed,
  sendEmailModalMethods,
} from '@/composables/send/sendEmailModalOptionsApi';

const CONTEXT_SECTION_COMPONENTS = {
  invoice: SendEmailInvoiceAttachmentsSection,
  payment_receipt: SendEmailInvoiceAttachmentsSection,
  expense_receipt: SendEmailInvoiceAttachmentsSection,
  simple_note: SendEmailInvoiceAttachmentsSection,
};

export default {
  name: 'SendEmailModal',
  components: {
    AppRichTextEditorField,
    SendEmailInvoiceAttachmentsSection,
  },
  props: {
    contextType: {
      type: String,
      default: 'invoice',
    },
    contextData: {
      type: Object,
      default: () => ({}),
    },
    show: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    allowExternalRecipients: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['close', 'submit'],
  data() {
    return createSendEmailModalState();
  },
  computed: {
    ...sendEmailModalComputed,
    resolvedContextType() {
      if (!CONTEXT_SECTION_COMPONENTS[this.contextType]) {
        return '';
      }

      return this.contextType;
    },
    activeContextSectionComponent() {
      return CONTEXT_SECTION_COMPONENTS[this.resolvedContextType] || null;
    },
  },
  watch: {
    show(visible) {
      if (!visible) {
        this.clearRecipientMenuTimer();
        this.activeRecipientField = '';
        return;
      }

      this.initializeContextState();
    },
    contextType() {
      if (this.show) {
        this.initializeContextState();
      }
    },
    contextData: {
      deep: true,
      handler() {
        if (this.show) {
          this.initializeContextState();
        }
      },
    },
  },
  beforeUnmount() {
    this.clearRecipientMenuTimer();
  },
  methods: sendEmailModalMethods,
};
</script>

<style lang="scss" src="@/assets/scss/domains/app/components/common/_send-email-modal.scss"></style>